import { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import core, { App } from '@gin-betting/core';
import filters, { Filters } from '@gin-betting/filters';
import matchList from '@gin-betting/match-list';
import { createStructuredSelector } from 'reselect';

import { useDidUpdate, usePrevious } from 'hooks';
import { State } from 'types';
import { isFavoritePage, isFeaturedPage } from 'utils';
import type { NextFilters } from '../../types';
import {
    filterMatches,
    getNextOpenedSportIds,
    getNextPathname,
    getRequestedSportType,
    getSportsWithCheckedTournaments,
} from '../../utils';

const {
    services: { stringify },
    utils: {
        isInnerPage,
        checkOnRoute,
        getAppliedFilters,
        getFiltersFromLocation,
    },
    hooks: { useDeepMemo },
    entities: {
        selectors: { selectSports },
    },
} = core;

const {
    selectors: { selectCategorizerData },
} = filters;

const {
    pagination: {
        actions: { dropPaginationState },
    },
} = matchList;

const selectProps = createStructuredSelector({
    sports: selectSports,
    categorizerItems: selectCategorizerData,
});

interface Output {
    tournamentIds: string[];
    openedSportIds: string[];
    sportIds: string[];
    handleFiltersChange: (sportIds: string[], tournamentIds: string[]) => void;
    handleSportPreviewClick: (
        triggeredSportId: string,
        actionType: Filters.CheckGameType
    ) => void;
}

const useCategorizer = (): Output => {
    const history = useHistory();
    const dispatch = useDispatch();

    const { location } = history;

    const appliedFilters = useDeepMemo(getAppliedFiltersWithDefault());

    const { sportIds, tournamentIds } = appliedFilters;

    const [openedSportIds, setOpenedSportIds] = useState<string[]>(sportIds);

    const { sports, categorizerItems } = useSelector((state: State) =>
        selectProps(state, {})
    );

    const categorizerItemsRef = useRef(categorizerItems);
    const prevCategorizerItems = usePrevious(categorizerItems);

    categorizerItemsRef.current = categorizerItems;

    const clearState = useCallback((callback: AnyFn) => {
        callback();
    }, []);

    useDidUpdate(() => {
        setOpenedSportIds(getAppliedFiltersWithDefault().sportIds);
    }, [location.pathname, location.search]);

    useDidUpdate(() => {
        if (
            prevCategorizerItems?.length ||
            !categorizerItemsRef.current.length
        ) {
            return;
        }

        setOpenedSportIds(prevState => {
            if (prevState.length) return prevState;

            const sportIdsByTournaments = getSportsWithCheckedTournaments(
                categorizerItemsRef.current,
                tournamentIds
            );

            return [...sportIds, ...sportIdsByTournaments];
        });
    }, [tournamentIds, sportIds]);

    const setupFiltersIntoUrl: typeof filterMatches = useCallback(
        nextFilters => {
            const { page } = getFiltersFromLocation();

            if (page && +page > 1) {
                dispatch(dropPaginationState());
            }

            const requestedSportType = getRequestedSportType(
                nextFilters.sportIds || [],
                sports
            );

            const nextPathname = requestedSportType
                ? getNextPathname(requestedSportType)
                : undefined;

            filterMatches(nextFilters, nextPathname);
        },
        [dispatch, sports]
    );

    const goToUpcomingWithSearch = useCallback(
        (search: NextFilters): void => {
            const pathnameRout = checkOnRoute(App.Routes.OUTRIGHT)
                ? App.Routes.OUTRIGHTS_LIST
                : App.Routes.UPCOMING_LIST;

            clearState(() =>
                history.push({
                    ...location,
                    pathname: pathnameRout,
                    search: search ? stringify(search) : '',
                })
            );
        },
        [clearState, history, location]
    );

    const onSelect = useCallback(
        ({
            sportIds: currentSportIds,
            tournamentIds: currentTournamentIds,
        }: NextFilters) => {
            if (isInnerPage() || isFavoritePage() || isFeaturedPage()) {
                return goToUpcomingWithSearch({
                    sportIds: currentSportIds,
                    tournamentIds: currentTournamentIds,
                });
            }

            setupFiltersIntoUrl({
                sportIds: currentSportIds,
                tournamentIds: currentTournamentIds,
                matchStatuses: [],
                freeBetId: '',
            });
        },
        [goToUpcomingWithSearch, setupFiltersIntoUrl]
    );

    const handleFiltersChange = useCallback(
        (nextSportIds: string[], nextTournamentIds: string[]): void => {
            onSelect({
                sportIds: nextSportIds,
                tournamentIds: nextTournamentIds,
            });
        },
        [onSelect]
    );

    const handleSportPreviewClick = useCallback(
        (triggeredSportId: string, actionType: Filters.CheckGameType) => {
            const nextOpenedSportIds = getNextOpenedSportIds({
                actionType,
                openedSportIds,
                selectedSportIds: sportIds,
                triggeredSportId,
            });

            setOpenedSportIds(nextOpenedSportIds);
        },
        [openedSportIds, sportIds]
    );

    return {
        tournamentIds,
        openedSportIds,
        sportIds,
        handleFiltersChange,
        handleSportPreviewClick,
    };
};

function getAppliedFiltersWithDefault(): Pick<
    Required<ReturnType<typeof getAppliedFilters>>,
    'sportIds' | 'tournamentIds'
> {
    const { sportIds = [], tournamentIds = [] } = getAppliedFilters();

    return {
        sportIds,
        tournamentIds,
    };
}

export default useCategorizer;
